export type SeoRouteConfig = {
  title: string;
  description: string;
  robots: 'index, follow' | 'noindex, nofollow';
  canonical?: string;
};

export const SEO_CANONICAL_URL = 'https://prowycenaapp.pl/';
export const SEO_LANDING_TITLE = 'ProWycena — robocze wyceny remontów i Tarcza Marży';
export const SEO_LANDING_DESCRIPTION =
  'Twórz robocze wyceny malowania, gładzi i prac wykończeniowych. Tarcza Marży przypomina o pozycjach do decyzji, wykonawca zatwierdza metraż, stawki i ceny, a klient otrzymuje czytelny PDF.';

export const FAQ_ENTRIES = [
  {
    question: 'Czy ProWycena mierzy pomieszczenie ze zdjęcia?',
    answer: 'Nie. Zdjęcie może pomóc wskazać kontekst i ryzyka. Wymiary wpisuje lub zatwierdza wykonawca.',
  },
  {
    question: 'Czy muszę zakładać konto?',
    answer: 'Nie, aby przygotować pierwszą roboczą wycenę i testowy PDF. Konto służy do zapisu projektu i późniejszego powrotu.',
  },
  {
    question: 'Czy Tarcza Marży automatycznie zwiększa cenę?',
    answer: 'Nie. Tylko pozycje świadomie dodane przez wykonawcę wpływają na sumę.',
  },
  {
    question: 'Czy ProWycena wycenia generalny remont łazienki?',
    answer: 'Nie w obecnym MVP. Tryb płytek dotyczy uproszczonej wyceny układania okładziny.',
  },
  {
    question: 'Jakie roboty obsługuje obecna wersja?',
    answer: 'Malowanie i gładź, uproszczone płytki oraz bruk.',
  },
  {
    question: 'Czy PDF pokazuje marżę wykonawcy?',
    answer: 'Nie. PDF klienta pokazuje zakres i cenę, bez danych wewnętrznych.',
  },
  {
    question: 'Czy materiały są automatycznie doliczane?',
    answer: 'Nie. Użytkownik wybiera, czy materiały są po stronie klienta, wykonawcy czy mają charakter informacyjny.',
  },
  {
    question: 'Czy stawki są obowiązkowe?',
    answer: 'Nie. Stawki orientacyjne są punktem odniesienia, a wykonawca może zatwierdzić lub wpisać własną.',
  },
] as const;

const SEO_APP_DESCRIPTION = 'Robocza część aplikacji ProWycena dla zalogowanych użytkowników.';

const PUBLIC_ROUTES: Record<string, SeoRouteConfig> = {
  '/o-produkcie': {
    title: 'O produkcie — ProWycena dla ekip wykończeniowych',
    description: 'Poznaj aktualny zakres ProWyceny: robocze wyceny malowania, gładzi, płytek i bruku oraz PDF dla klienta.',
    robots: 'index, follow',
    canonical: `${SEO_CANONICAL_URL}o-produkcie`,
  },
  '/tarcza-marzy': {
    title: 'Tarcza Marży — sprawdź pominięte koszty | ProWycena',
    description: 'Tarcza Marży pokazuje typowe pominięcia w wycenie. Wykonawca decyduje, co dodać do ceny i PDF.',
    robots: 'index, follow',
    canonical: `${SEO_CANONICAL_URL}tarcza-marzy`,
  },
  '/faq': {
    title: 'FAQ — pytania o ProWycenę',
    description: 'Odpowiedzi o pomiarach, koncie, Tarczy Marży, materiałach, stawkach, obsługiwanych pracach i PDF.',
    robots: 'index, follow',
    canonical: `${SEO_CANONICAL_URL}faq`,
  },
};

export function resolveSeoRoute(pathname: string): SeoRouteConfig {
  if (pathname === '/') {
    return {
      title: SEO_LANDING_TITLE,
      description: SEO_LANDING_DESCRIPTION,
      robots: 'index, follow',
      canonical: SEO_CANONICAL_URL,
    };
  }

  const publicRoute = PUBLIC_ROUTES[pathname];
  if (publicRoute) return publicRoute;

  if (pathname === '/kontakt') {
    return {
      title: 'Kontakt — ProWycena',
      description: 'Kontakt mailowy w sprawach testów beta ProWycena.',
      robots: 'index, follow',
      canonical: `${SEO_CANONICAL_URL}kontakt`,
    };
  }
  if (pathname === '/regulamin') {
    return {
      title: 'Regulamin testów beta — ProWycena',
      description: 'Roboczy regulamin testów beta ProWycena dla wykonawców.',
      robots: 'index, follow',
      canonical: `${SEO_CANONICAL_URL}regulamin`,
    };
  }
  if (pathname === '/regulamin-testow-beta') {
    return { title: 'Regulamin testów beta — ProWycena', description: 'Roboczy regulamin testów beta ProWycena dla wykonawców.', robots: 'noindex, nofollow' };
  }
  if (pathname === '/polityka-prywatnosci') {
    return {
      title: 'Polityka prywatności — ProWycena',
      description: 'Polityka prywatności wersji testowej ProWycena.',
      robots: 'index, follow',
      canonical: `${SEO_CANONICAL_URL}polityka-prywatnosci`,
    };
  }
  if (pathname === '/login') {
    return { title: 'Logowanie — ProWycena', description: 'Logowanie do panelu wykonawcy ProWycena.', robots: 'noindex, nofollow' };
  }
  if (pathname === '/wycena' || pathname.startsWith('/app/wycena')) {
    return { title: 'Szybka wycena — ProWycena', description: 'Publiczna szybka wycena robocza. Metraż i zakres zatwierdza wykonawca.', robots: 'noindex, nofollow' };
  }
  if (pathname.startsWith('/quote/')) {
    return { title: 'Podgląd wyceny — ProWycena', description: 'Prywatny podgląd wyceny udostępniony klientowi.', robots: 'noindex, nofollow' };
  }
  if (pathname.startsWith('/offer/')) {
    return { title: 'Oferta klienta — ProWycena', description: 'Prywatna oferta robocza udostępniona klientowi.', robots: 'noindex, nofollow' };
  }

  return { title: 'Aplikacja wykonawcy — ProWycena', description: SEO_APP_DESCRIPTION, robots: 'noindex, nofollow' };
}
